<script setup lang="ts">
import type { FormSubmitEvent, SelectItem } from '@nuxt/ui'
import * as z from 'zod'

const props = defineProps<{
  locationName?: string
}>()

const content = {
  title: computed(() => `Vergelijk alle makelaars in ${props.locationName || 'Nederland'}`),
  description: computed(
    () =>
      `Vergelijk prestaties en tarieven van makelaars, en vind de beste om je huis te verkopen in ${
        props.locationName || 'Nederland'
      }.`,
  ),
  features: ['Data-gedreven aanbevelingen', 'Geen verplichtingen', 'Snel & eenvoudig'],
  postcode: {
    label: 'Jouw postcode',
    placeholder: 'bijv. 1234 AB',
  },
  button: 'Vergelijk makelaars',
}

const buttonUi = {
  base: 'w-full justify-center cursor-pointer',
}

interface LocationResponse {
  street: string
  streetNen: string
  startHouseNumber: string
  endHouseNumber: string
  houseNumberType: string
  city: string
  cityShort: string
  cityId: string
  municipality: string
  municipalityShort: string
  municipalityId: string
  province: string
}

const location = ref<LocationResponse | null>(null)
const loading = ref(false)
const error = ref('')
const { $posthog } = useNuxtApp()

const brokerTypes: SelectItem[] = [
  {
    label: 'Aankoop',
    value: 'vergelijk-aankoop',
  },
  {
    label: 'Verkoop',
    value: 'vergelijk-verkoop',
  },
  {
    label: 'Taxatie',
    value: 'vergelijk-taxatie',
  },
]

const schema = z.object({
  postcode: z.string().min(1, 'Postcode is verplicht'),
  brokerType: z.string(),
})

type Schema = z.output<typeof schema>

const state = reactive<Schema>({
  postcode: '',
  brokerType: 'vergelijk-aankoop',
})

const isValidPostcode = (postcode: string) => {
  const cleanPostcode = postcode.replace(/\s/g, '')
  return /^\d{4}[a-zA-Z]{2}$/.test(cleanPostcode)
}

const fetchLocation = async (postcode: string) => {
  if (!isValidPostcode(postcode)) {
    location.value = null
    error.value = ''
    return
  }

  loading.value = true
  error.value = ''

  try {
    const cleanPostcode = postcode.replace(/\s/g, '')
    const response = await $fetch<LocationResponse[]>(`https://postcode.bartligthart.workers.dev/?postcode=${cleanPostcode}`)

    if (!response?.length) {
      throw new Error('Geen locatie gevonden voor deze postcode')
    }

    const locationData = response[0]
    if (!locationData) {
      throw new Error('Geen locatie gevonden voor deze postcode')
    }

    location.value = locationData
    error.value = ''
  }
  catch {
    location.value = null
    error.value = 'Geen locatie gevonden voor deze postcode'
  }
  finally {
    loading.value = false
  }
}

watch(() => state.postcode, (newPostcode) => {
  if (newPostcode) {
    fetchLocation(newPostcode)
  }
  else {
    location.value = null
    error.value = ''
  }
})

const onSubmit = (event: FormSubmitEvent<Schema>) => {
  event.preventDefault()
  const brokerType = event.data.brokerType

  $posthog().capture('sidebar_form_submit', {
    form_type: 'compare_agents',
    broker_type: brokerType,
    postcode: event.data.postcode,
    city: location.value?.city,
    province: location.value?.province,
    location_name: props.locationName,
  })

  useRouter().push(`/${brokerType}/?postcode=${event.data.postcode}`)
}
</script>

<template>
  <div class="bg-gray-50 rounded-xl p-8 mb-8 border border-gray-200">
    <h2 class="text-2xl font-semibold mb-4">
      {{ content.title }}
    </h2>

    <p class="text-gray-700 mb-6">
      {{ content.description }}
    </p>

    <ul class="space-y-3 mb-8">
      <li
        v-for="feature in content.features"
        :key="feature"
        class="flex items-center"
      >
        <UIcon
          name="i-heroicons-check-circle"
          class="mr-2 text-lg text-gray-700"
        />
        <span class="text-gray-700">{{ feature }}</span>
      </li>
    </ul>

    <UForm
      class="space-y-4"
      :schema="schema"
      :state="state"
      @submit="onSubmit"
    >
      <div>
        <div class="grid grid-cols-2 gap-2">
          <UFormField
            label="Postcode"
            name="postcode"
          >
            <UInput
              v-model="state.postcode"
              :placeholder="content.postcode.placeholder"
              size="lg"
            />
          </UFormField>

          <UFormField
            label="Makelaar voor:"
            name="brokerType"
          >
            <USelect
              v-model="state.brokerType"
              :items="brokerTypes"
              class="w-full"
              size="lg"
            />
          </UFormField>
        </div>
      </div>
      <UButton
        variant="solid"
        color="primary"
        size="lg"
        :ui="buttonUi"
        type="submit"
      >
        {{ content.button }}
      </UButton>
      <div
        v-if="location"
        class="flex items-center"
      >
        <UIcon
          name="i-heroicons-check-circle"
          class="mr-2 text-lg text-success"
        />
        <span class="text-success font-medium text-sm">{{ location?.street }}, {{ location?.city }} gevonden</span>
      </div>
    </UForm>
  </div>
</template>
